import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiPackage, FiAlertTriangle } from 'react-icons/fi';
import { stockService } from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import StatCard from '../components/StatCard';
import { formatCurrency, getStockStatus } from '../utils/formatters';

const Stock = () => {
  const navigate = useNavigate();
  const [currency, setCurrency] = useState('USD');

  const { data, isLoading, error } = useQuery(
    'stock-levels',
    stockService.getLevels
  );

  if (isLoading) return <LoadingSpinner message="Loading stock..." />;

  if (error) {
    return (
      <div className="empty-state">
        <FiPackage size={48} />
        <h3>Failed to load stock</h3>
        <p>{error.message}</p>
      </div>
    );
  }

  const stockData = data?.data || [];
  const summary = data?.summary || {};

  const isLrd = currency === 'LRD';
  const currencySymbol = isLrd ? 'L$' : '$';

  const totalValue = isLrd
    ? summary.total_value_lrd ?? 0
    : summary.total_value_usd ?? summary.total_value ?? 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Stock Management</h1>
          <p className="page-subtitle">
            Monitor and manage your inventory
          </p>
        </div>
        <div className="page-actions">
          {/* Currency toggle */}
          <div
            style={{
              display: 'flex',
              gap: '4px',
              background: '#f3f4f6',
              padding: '4px',
              borderRadius: '8px',
              marginRight: '8px',
            }}
          >
            <button
              className={`btn ${currency === 'USD' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCurrency('USD')}
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              USD $
            </button>
            <button
              className={`btn ${currency === 'LRD' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCurrency('LRD')}
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              LRD L$
            </button>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/stock/receive')}
          >
            <FiPlus size={18} />
            Receive Stock
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard
          icon={<FiPackage size={24} />}
          value={summary.total_products || 0}
          label="Total Products"
          color="blue"
        />
        <StatCard
          icon={<FiPackage size={24} />}
          value={summary.total_units || 0}
          label="Total Units"
          color="purple"
        />
        <StatCard
          icon={<FiPackage size={24} />}
          value={`${currencySymbol}${parseFloat(totalValue).toFixed(2)}`}
          label={`Stock Value (${currency})`}
          color="green"
        />
        <StatCard
          icon={<FiAlertTriangle size={24} />}
          value={(summary.low_stock || 0) + (summary.out_of_stock || 0)}
          label="Need Attention"
          color="orange"
        />
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Category</th>
              <th>Current Stock</th>
              <th>Reorder Level</th>
              <th>Stock Value ({currency})</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {stockData.map((item) => {
              const status = getStockStatus(
                item.current_stock || 0,
                item.reorder_level || 0
              );

              // Pick the right value for the selected currency
              const stockValue = isLrd
                ? parseFloat(item.stock_value_lrd || 0)
                : parseFloat(
                    item.stock_value_usd ?? item.stock_value ?? 0
                  );

              return (
                <tr key={item.product_id}>
                  <td>
                    <strong>{item.product_name}</strong>
                  </td>
                  <td>{item.category || '-'}</td>
                  <td>
                    <strong>{item.current_stock || 0}</strong>
                  </td>
                  <td>{item.reorder_level || 0}</td>
                  <td>
                    {currencySymbol}
                    {stockValue.toFixed(2)}
                  </td>
                  <td>
                    <span className={`badge badge-${status.color}`}>
                      {status.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Stock;
